'use client';

import React, { useEffect, useRef, useState } from 'react';

import styles from './styles.module.css';

import { initGoogleIdentityClient } from '@/modules/auth/google';
import { useGoogleIdentityService } from '@/modules/auth/useGoogleIdentityService';
import { useCurrentLanguage } from '@/modules/language/useCurrentLanguage';
import { getLogger } from '@/modules/observability/logging';

const LANGUAGE_LOCALES = {
  en: 'en-GB',
  it: 'it-IT',
  de: 'de-DE',
  fr: 'fr-FR',
};

type Props = {
  handleResponse: (response: google.accounts.id.CredentialResponse) => void;
  nonce: string;
  variant: 'eyeCatching' | 'icon';
  onSubmitTracking?: () => void;
};

export function GoogleButton({
  handleResponse,
  nonce,
  variant,
  onSubmitTracking,
}: Props) {
  const { language } = useCurrentLanguage();
  const { isGoogleIdentityLoaded } = useGoogleIdentityService(nonce);
  const [hasErrored, setHasErrored] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement | null>(null);

  function onButtonClick() {
    onSubmitTracking && onSubmitTracking();
  }

  useEffect(() => {
    const googleId = window.google?.accounts?.id;
    if (isGoogleIdentityLoaded && googleButtonRef.current && googleId) {
      initGoogleIdentityClient(handleResponse);
      try {
        googleId.renderButton(googleButtonRef.current, {
          theme: 'outline',
          size: 'large',
          width: variant === 'icon' ? 42 : 343,
          text: 'continue_with',
          type: variant === 'icon' ? 'icon' : 'standard',
          locale:
            LANGUAGE_LOCALES[language as keyof typeof LANGUAGE_LOCALES] ||
            'en-GB',
          click_listener: onButtonClick,
          shape: 'pill',
        });
      } catch {
        setHasErrored(true);
        getLogger().error('Google button failed to render');
      }
    }
  }, [isGoogleIdentityLoaded]);

  if (hasErrored) {
    return null;
  }

  return (
    <div
      ref={googleButtonRef}
      // eslint-disable-next-line no-restricted-syntax
      data-testid="google-button"
      className={styles.googleButton}
    />
  );
}
